/*
 * hpq-cart-totals.js — sole renderer for subtotal/savings/promo/total (+ free-ship line).
 *
 * In draft-checkout mode (HPQ_CONFIG.draftCheckoutEnabled === true), this module
 * owns all output that sits next to the totals wrapper on cart-page and quick-cart.
 * It consumes the single hpqCartState event stream and updates every
 * [data-hpq-totals-root] with one DOM write when values change.
 */
(function () {
  'use strict';

  function toInt(value) {
    return typeof value === 'number' && isFinite(value) ? Math.max(0, value | 0) : 0;
  }

  // Escape any value that reaches innerHTML. The money formatter is derived by SAMPLING the
  // theme's server-rendered total text (prefix/suffix/ISO), which we treat as untrusted — a
  // crafted localization/money-format string must never become executable markup in a row.
  var ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) { return ESC_MAP[ch]; });
  }

  function addGrouping(value, sep) {
    if (!sep) return value;
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  function sampleMoneyFormatter(sampleText, currency) {
    var text = String(sampleText == null ? '' : sampleText).replace(/\u00a0/g, ' ').trim();
    if (!text) return null;

    var isoMatch = text.match(/\s([A-Z]{3})\s*$/);
    var iso = '';
    if (isoMatch) {
      iso = isoMatch[1];
      text = text.slice(0, -isoMatch[0].length).trim();
    }

    var numMatch = text.match(/-?\d[\d\s,'’\.]*/);
    if (!numMatch) return null;

    var prefix = text.slice(0, numMatch.index);
    var valueText = numMatch[0];
    var suffix = text.slice(numMatch.index + numMatch[0].length);

    // Classify each separator as DECIMAL vs GROUPING by how many digits trail it. A money
    // decimal separator is followed by exactly 1–2 digits at the very end ("59.00", "1.234,5");
    // a separator trailed by 3 digits is a thousands group ("1,234", "1.234.567"). This keeps
    // "$1,234 USD" (grouped, no decimals) from being misread as a decimal comma — which
    // previously rendered 123400 cents as "$1234,00" instead of "$1,234.00".
    var lastDot = valueText.lastIndexOf('.');
    var lastComma = valueText.lastIndexOf(',');
    var lastSep = Math.max(lastDot, lastComma);
    var decimalSep = '.'; // rendering default (USD-style .00) unless a real decimal is detected
    var groupSep = '';
    var apos = /’/.test(valueText) ? '’' : (/'/.test(valueText) ? "'" : '');
    if (lastSep !== -1) {
      var sepChar = valueText.charAt(lastSep);
      var trailingDigits = valueText.slice(lastSep + 1).replace(/[^\d]/g, '').length;
      var otherChar = sepChar === '.' ? ',' : '.';
      var hasOther = valueText.indexOf(otherChar) !== -1;
      if (trailingDigits === 1 || trailingDigits === 2) {
        decimalSep = sepChar;                                       // real decimal separator
        groupSep = hasOther ? otherChar : (apos || (/\s/.test(valueText) ? ' ' : ''));
      } else {
        decimalSep = '.';                                          // grouping only, no fraction
        groupSep = sepChar;
      }
    } else if (apos) {
      groupSep = apos;                                             // apostrophe grouping, no decimal
    } else if (/\s/.test(valueText)) {
      groupSep = ' ';
    }

    // Validate the numeric core parses (defends against a garbage sample).
    var probe = valueText.replace(/[\s'’]/g, '');
    probe = (decimalSep === ',')
      ? probe.replace(/\./g, '').replace(/,/g, '.')
      : probe.replace(/,/g, '');
    var parsed = parseFloat(probe);
    if (!isFinite(parsed)) return null;
    if (parsed < 0) parsed = 0;

    return function (cents) {
      var amount = toInt(cents);
      var sign = amount < 0 ? '-' : '';
      var abs = Math.abs(amount);
      var raw = (abs / 100).toFixed(2);
      var parts = raw.split('.');
      var ints = addGrouping(parts[0], groupSep);
      var rendered = ints + '.' + parts[1];
      if (decimalSep === ',') {
        rendered = ints + ',' + parts[1];
      }

      return (
        prefix +
        sign +
        rendered +
        suffix +
        (iso ? ' ' + iso : '')
      );
    };
  }

  // Money is formatted for the store's MARKET, not the shopper's browser locale — a US customer
  // on a non-US browser must still see US formatting ($1,234.50, not 1.234,50). Locked to the
  // current market (United States) today. To add a market later (Canada/CAD is the likely next),
  // add one entry here or set HPQ_CONFIG.moneyLocale from Shopify localization — nothing else
  // changes. This must never regress the current US market for the sake of future adaptability.
  var MONEY_LOCALES = { USD: 'en-US', CAD: 'en-CA' };
  function moneyLocaleFor(currencyCode) {
    var override = (typeof window !== 'undefined' && window.HPQ_CONFIG && window.HPQ_CONFIG.moneyLocale) || '';
    if (override) return override;
    return MONEY_LOCALES[currencyCode] || 'en-US';
  }

  function fallbackMoneyFormatter(currencyCode, isoHint) {
    return function (cents) {
      var fmt;
      var value = (toInt(cents) / 100).toFixed(2);
      try {
        fmt = new Intl.NumberFormat(moneyLocaleFor(currencyCode || 'USD'), {
          style: 'currency',
          currency: currencyCode || 'USD'
        }).format(toInt(cents) / 100);
        return (isoHint ? fmt + ' ' + isoHint : fmt);
      } catch (err) {
        return '$' + value + (isoHint ? ' ' + isoHint : '');
      }
    };
  }

  function resolveMoneyFormatter(root, vm) {
    var cache = root.__hpqMoney || {};
    var currency = vm && vm.currency ? vm.currency : 'USD';
    if (cache.currency === currency && cache.formatter) {
      return cache.formatter;
    }

    var wrapper = null;
    var isQuickCart = isInQuickCart(root);
    if (!isQuickCart) {
      var wrapperInfo = ownershipState(root);
      wrapper = wrapperInfo.wrapper || null;
    }

    var sample = null;
    if (wrapper) {
      sample = sampleWrapperTotal(wrapper);
    }

    var formatter = sampleMoneyFormatter(sample, currency);
    if (!formatter) {
      var suffixHint = '';
      var suffixMatch = typeof sample === 'string' ? sample.match(/[A-Z]{3}$/) : null;
      if (suffixMatch) suffixHint = suffixMatch[0];
      formatter = fallbackMoneyFormatter(currency, suffixHint);
    }

    root.__hpqMoney = { currency: currency, formatter: formatter };
    return formatter;
  }

  function sampleWrapperTotal(wrapper) {
    if (!wrapper || typeof wrapper.querySelector !== 'function') return '';
    var totalSpan = wrapper.querySelector('[data-hpq-cart-total]') || wrapper.querySelector('.cart__footer-total span');
    return totalSpan && (totalSpan.textContent || totalSpan.innerText || '') || '';
  }

  function buildMoneyString(vm, formatter) {
    var m = formatter || fallbackMoneyFormatter((vm && vm.currency) || 'USD');
    return m;
  }

  function isInQuickCart(root) {
    var node = root;
    while (node && node !== document) {
      if (node.className && typeof node.className === 'string' && node.className.indexOf('quick-cart') !== -1) return true;
      node = node.parentElement;
    }
    return false;
  }

  function ownershipState(root) {
    var prev = root && root.previousElementSibling;
    var hasWrapper = !!(
      prev &&
      ((typeof prev.matches === 'function' && prev.matches('[js-cart-footer-subtotal-wrapper]')) ||
        prev.getAttribute && prev.getAttribute('js-cart-footer-subtotal-wrapper') !== null)
    );
    return {
      wrapper: hasWrapper ? prev : null,
      ownsSurface: hasWrapper
    };
  }

  function moneyFormatterFor(vm) {
    var currency = (vm && vm.currency) || 'USD';
    // Match the theme's own money format (currency code included, e.g. "$107.90 USD") by parsing a
    // theme-rendered sample exposed in HPQ_CONFIG.moneySample. This keeps the JS-owned drawer
    // visually identical to the theme's {{ ... | money }} output and adapts to whatever market the
    // theme is configured for. Fall back to a market-locked (en-US) formatter if no sample exists.
    var sample = (typeof window !== 'undefined' && window.HPQ_CONFIG && window.HPQ_CONFIG.moneySample) || '';
    var sampled = sample ? sampleMoneyFormatter(sample, currency) : null;
    if (sampled) return sampled;
    return function (cents) {
      return fallbackMoneyFormatter(currency)(cents);
    };
  }

  function buildFreeShipText(fs, money) {
    if (!fs || !fs.status) return '';
    if (fs.status === 'free') return '✓ FREE SHIPPING';
    if (fs.status === 'away') {
      return 'Add <b>' + esc(money(fs.remainingCents)) + '</b> more for <b>FREE</b> shipping';
    }
    if (fs.status === 'pending') return 'Checking shipping…';
    return '';
  }

  function buildTotalsHTML(vm, formatter, isQuickCartRoot) {
    var m = buildMoneyString(vm, formatter);
    var subtotalCents = toInt(vm && vm.subtotalCents);
    var savingsCents = toInt(vm && vm.automaticSavingsCents);
    var promoCents = toInt(vm && vm.promoDiscountCents);
    var totalCents = toInt(vm && vm.totalCents);

    var surface = isQuickCartRoot ? 'quick-cart' : 'cart';
    var savingsRowClass = surface + '__footer-savings';
    var subtotalRowClass = surface + '__footer-subtotal';
    var totalRowClass = surface + '__footer-total';

    var out = '';
    out += '<div class="' + subtotalRowClass + '">';
    out += '<h3 class="ff-body fs-body-200">Subtotal</h3>';
    out += '<span class="ff-body fs-body-200" data-hpq-cart-subtotal>' + esc(m(subtotalCents)) + '</span>';
    out += '</div>';

    if (savingsCents > 0) {
      out += '<div class="' + savingsRowClass + '">';
      out += '<h3 class="ff-body fs-body-200">Savings</h3>';
      out += '<span class="ff-body fs-body-200" data-hpq-cart-savings>-' + esc(m(savingsCents)) + '</span>';
      out += '</div>';
    }

    if (promoCents > 0) {
      out += '<div class="' + savingsRowClass + '">';
      out += '<h3 class="ff-body fs-body-200">Promo</h3>';
      out += '<span class="ff-body fs-body-200" data-hpq-cart-promo>-' + esc(m(promoCents)) + '</span>';
      out += '</div>';
    }

    out += '<div class="' + totalRowClass + '">';
    out += '<h3 class="ff-body fs-body-200">Total</h3>';
    out += '<span class="ff-body fs-body-200" data-hpq-cart-total>' + esc(m(totalCents)) + '</span>';
    out += '</div>';

    // The drawer already renders a prominent free-shipping progress bar at the top of the footer;
    // a second copy under Total would duplicate it (and would show flag-off too). Only the cart
    // page — which has no such bar beside its totals — gets the under-total free-ship line.
    var freeShipText = isQuickCartRoot ? '' : buildFreeShipText(vm && vm.freeShip, m);
    if (freeShipText) {
      var freeClass = 'hpq-cart-coupon__freeship ff-body fs-body-75';
      var isFree = vm.freeShip && vm.freeShip.status === 'free' && !vm.freeShip.pending;
      if (isFree) freeClass += ' hpq-cart-coupon__freeship--free';
      out += '<p class="' + freeClass + '" data-hpq-freeship>' + freeShipText + '</p>';
    }

    return out;
  }

  function signature(vm) {
    var fs = (vm && vm.freeShip) || {};
    return [
      vm && vm.enabled ? '1' : '0',
      vm && vm.currency || '',
      toInt(vm && vm.subtotalCents),
      toInt(vm && vm.automaticSavingsCents),
      toInt(vm && vm.promoDiscountCents),
      toInt(vm && vm.totalCents),
      fs.pending ? '1' : '0',
      fs.status || '',
      toInt(fs.threshold),
      toInt(fs.remainingCents),
      toInt(fs.pct),
      vm && vm.promoCode || ''
    ].join('|');
  }

  // Cart-page ownership is expressed as a class on a STABLE ancestor (<html>), not by mutating
  // the theme's totals wrapper. A CSS rule hides [js-cart-footer-subtotal-wrapper] by selector,
  // so when Stiletto's async refreshCart() replaces that wrapper node with a fresh visible one,
  // the replacement is hidden the instant it lands — no MutationObserver, no re-inject, and the
  // theme node itself is never touched (better flag-off parity).
  function ownClass(on) {
    var el = (typeof document !== 'undefined' && document) ? document.documentElement : null;
    if (!el || !el.classList) return;
    if (on) el.classList.add('hpq-totals-owned');
    else el.classList.remove('hpq-totals-owned');
  }

  function ensureOwnCss() {
    if (typeof document === 'undefined' || !document) return;
    if (typeof document.getElementById !== 'function' || typeof document.createElement !== 'function') return;
    if (document.getElementById('hpq-totals-css')) return;
    var head = document.head || (typeof document.getElementsByTagName === 'function' && document.getElementsByTagName('head')[0]);
    if (!head || typeof head.appendChild !== 'function') return;
    var style = document.createElement('style');
    style.id = 'hpq-totals-css';
    // (1) hide the native wrapper (attribute selector → native only) while we own the surface.
    // (2)+(3) control the cart-page SIDECAR by class, NOT by the `hidden` attribute: the sidecar
    // carries `.cart__footer-subtotal-wrapper { display:flex }`, and an author display rule beats
    // the UA `[hidden]{display:none}` per the HTML spec — so `hidden` alone can't hide it. These
    // scoped rules ([data-hpq-totals-root] → sidecar only; the drawer root uses a different class)
    // hide it until we own the surface and show it (display:flex) once we do.
    style.textContent =
      'html.hpq-totals-owned [js-cart-footer-subtotal-wrapper]{display:none !important;}' +
      '[data-hpq-totals-root].cart__footer-subtotal-wrapper{display:none;}' +
      'html.hpq-totals-owned [data-hpq-totals-root].cart__footer-subtotal-wrapper{display:flex;}';
    head.appendChild(style);
  }

  // The store leaves rawItemCount/rawCartTotalCents null until /cart.js has actually populated a
  // cart. Claiming the cart-page surface before then would hide the correct server-rendered
  // totals and paint a zeroed VM ($0.00) on a slow or offline request.
  function hasCartData(vm) {
    return !!vm
      && vm.rawItemCount !== null && vm.rawItemCount !== undefined
      && vm.rawCartTotalCents !== null && vm.rawCartTotalCents !== undefined;
  }

  function renderTotals(root, vm) {
    if (!root || !vm) return;
    var quick = isInQuickCart(root);

    // QUICK-CART ROOT: the drawer footer has no native totals behind it, so this root is the
    // SOLE totals surface. Always render + show — flag-on or flag-off. computeTotals() with the
    // flag off yields plain Subtotal/Savings/Total (no promo), which is exactly the correct
    // flag-off drawer. (Hiding it here is what produced the empty flag-off drawer footer.)
    if (quick) {
      var sigQ = signature(vm);
      if (root.hidden) root.hidden = false;
      if (root.__hpqSig === sigQ) return;
      root.__hpqSig = sigQ;
      root.innerHTML = buildTotalsHTML(vm, moneyFormatterFor(vm), true);
      return;
    }

    // CART-PAGE SIDECAR: take over only when the flag is on AND the store has a real cart. Until
    // then the native wrapper stays visible (correct fallback); on fetch failure it stays there.
    if (!vm.enabled || !hasCartData(vm)) {
      ownClass(false);              // release → CSS un-hides the native wrapper
      if (!root.hidden) root.hidden = true;
      if (root.innerHTML) root.innerHTML = ''; // drop stale totals so nothing lingers behind CSS
      root.__hpqSig = null;         // force a fresh render when we next own the surface
      return;
    }

    ownClass(true);                 // hide [js-cart-footer-subtotal-wrapper] via the stable <html> class
    if (root.hidden) root.hidden = false;
    var sig = signature(vm);
    if (root.__hpqSig === sig) return;
    root.__hpqSig = sig;
    root.innerHTML = buildTotalsHTML(vm, resolveMoneyFormatter(root, vm), false);
  }

  var raf = 0;
  var queuedVm = null;
  var requestFrame = (typeof window.requestAnimationFrame === 'function') ? window.requestAnimationFrame : function (cb) { return setTimeout(cb, 16); };
  var cancelFrame = (typeof window.cancelAnimationFrame === 'function') ? window.cancelAnimationFrame : function (id) { clearTimeout(id); };

  function renderAll(vm) {
    if (!vm) return;
    var roots = (typeof document.querySelectorAll === 'function') ? document.querySelectorAll('[data-hpq-totals-root]') : [];
    var cartPageRootPresent = false;
    for (var i = 0; i < roots.length; i++) {
      if (!isInQuickCart(roots[i])) cartPageRootPresent = true;
      renderTotals(roots[i], vm);
    }
    // Safety net: if the theme wiped our cart-page sidecar entirely (a whole-footer re-render,
    // not just the wrapper), never leave the native wrapper hidden by our class with nothing
    // rendered in its place — release ownership so the server-rendered totals reappear.
    if (!cartPageRootPresent) ownClass(false);
  }

  function flushRender() {
    raf = 0;
    var vm = queuedVm;
    queuedVm = null;
    if (!vm && window.hpqCartState && typeof window.hpqCartState.getVM === 'function') {
      vm = window.hpqCartState.getVM();
    }
    if (!vm) return;
    renderAll(vm);
  }

  function queueRender(vm) {
    queuedVm = vm || queuedVm;
    if (raf) return;
    // flushRender() zeroes `raf` before rendering, so we never cancel the frame we're inside.
    raf = requestFrame(flushRender);
  }

  function readState() {
    return window.hpqCartState && typeof window.hpqCartState.getVM === 'function'
      ? window.hpqCartState.getVM()
      : null;
  }

  function onCartChanged(event) {
    queueRender(event && event.detail ? event.detail : null);
  }

  document.addEventListener('hpq:cart-changed', onCartChanged);

  ensureOwnCss();

  if (typeof window !== 'undefined' && window) {
    var initial = readState();
    queueRender(initial);
  }

  window.__hpqCartTotals = window.__hpqCartTotals || {};
  window.__hpqCartTotals.buildTotalsHTML = buildTotalsHTML;
  window.__hpqCartTotals.renderTotals = renderTotals;
  window.__hpqCartTotals.money = moneyFormatterFor;
  window.__hpqCartTotals._signature = signature;
  window.__hpqCartTotals._resolveMoneyFormatter = resolveMoneyFormatter;
})();
